import { SplitScreenLayout } from '@/components/layout/SplitScreenLayout/SplitScreenLayout';
import { EntityDocumentViewer_DocumentFragment } from '@/modules/documents/components/HouseholdDocumentsViewer/graphql/EntityDocumentViewer.generated';
import { DocumentSearchResult } from '@/modules/pdf/DocumentSearch/DocumentSearch.types';

import {
  HouseholdDocumentsViewerProvider,
  HouseholdDocumentsViewerProviderProps,
} from '../HouseholdDocumentsViewer/context/HouseholdDocumentsViewer.provider';
import { HouseholdDocumentsViewer } from '../HouseholdDocumentsViewer/HouseholdDocumentsViewer';

export interface SplitScreenLayoutWithDocumentsProps
  extends Omit<HouseholdDocumentsViewerProviderProps, 'householdId'> {
  householdId: string | null;
  rightContent: React.ReactNode;
  rightPane?: React.ReactNode;
  header?: React.ReactNode;
  footer: React.ReactNode;
  overrideAnnotations?: DocumentSearchResult[];
  overrideActiveAnnotationIndex?: number;
  overrideSearchQuery?: string;
  onUpdateSearchQuery?: () => void;
  // documentIds is used to override the documents that are shown in the documents viewer.
  documentIds?: string[];
  /** When true, automatically zoom the PDF to make the focused annotation readable. */
  autoZoom?: boolean;
  /**
   * An optional function to order the documents in the viewer.
   * @param docs
   */
  documentOrderFn?: (
    docs: EntityDocumentViewer_DocumentFragment[]
  ) => EntityDocumentViewer_DocumentFragment[];
  /** Primary client who dies first – used to initialize disposition diagram */
  initialPrimaryClientId?: string;
  showDiagram?: boolean;
  updateActiveDocumentId?: (documentId: string) => void;
}

export function SplitScreenLayoutWithDocuments({
  householdId,
  rightContent,
  rightPane,
  header,
  footer,
  overrideAnnotations,
  overrideActiveAnnotationIndex,
  overrideSearchQuery,
  onUpdateSearchQuery,
  overrideSearchLoading,
  documentIds,
  autoZoom,
  showDiagram = false,
  initialPrimaryClientId,
  updateActiveDocumentId,
  ...props
}: SplitScreenLayoutWithDocumentsProps) {
  if (!householdId) {
    return null;
  }

  return (
    <HouseholdDocumentsViewerProvider
      householdId={householdId}
      documentIds={documentIds}
      initialPrimaryClientId={initialPrimaryClientId}
      showDiagram={showDiagram}
      onUpdateActiveDocumentId={
        updateActiveDocumentId
          ? ({ documentId }) => updateActiveDocumentId(documentId)
          : undefined
      }
      {...props}
    >
      <SplitScreenLayout
        leftContent={
          householdId && (
            <HouseholdDocumentsViewer
              overrideAnnotations={overrideAnnotations}
              overrideActiveAnnotationIndex={overrideActiveAnnotationIndex}
              overrideSearchQuery={overrideSearchQuery}
              onUpdateSearchQuery={onUpdateSearchQuery}
              overrideSearchLoading={overrideSearchLoading}
              initialPrimaryClientId={initialPrimaryClientId}
              showDiagram={showDiagram}
              autoZoom={autoZoom}
            />
          )
        }
        rightContent={rightContent}
        rightPane={rightPane}
        header={header}
        footer={footer}
      />
    </HouseholdDocumentsViewerProvider>
  );
}
